import { Tooltip } from '@/shared/components/Tooltip';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  useTheme,
} from '@mui/material';
import { ReactElement } from 'react';
import { SIDEBAR } from '../constants';

interface Props {
  open: boolean;
  text?: string;
  displayText?: ReactElement;
  action: () => void;
  icon?: JSX.Element;
  ariaLabel?: string;
  selected?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  outlined?: boolean;
}

export const MenuItem = ({
  open,
  text,
  displayText,
  action,
  icon,
  ariaLabel,
  selected,
  disabled,
  disabledTooltip,
  outlined,
}: Props) => {
  const theme = useTheme();

  const textColor = theme.palette.neutralGrey[700];
  const disabledTextColor = theme.palette.grey[300];

  const tooltipTitle = disabled && disabledTooltip ? disabledTooltip : text;
  const disableTooltipHover = disabled && disabledTooltip ? false : open || !text;

  return (
    <ListItem
      disablePadding
      sx={{
        minWidth: '48px',
      }}
    >
      <Tooltip
        title={tooltipTitle}
        disableHoverListener={disableTooltipHover}
        placement="right"
        offset={8}
        enterDelay={300}
        enterNextDelay={300}
        disableTouchListener={true}
      >
        <Box sx={{ width: '100%' }}>
          <ListItemButton
            onClick={action}
            className={open ? 'list-item-button-open' : 'list-item-button-close'}
            disabled={disabled}
            sx={{
              outline: outlined ? '1px solid white' : 'none',
              opacity: `${disabled ? '0.5' : '1'} !important`,
              justifyContent: !icon && outlined ? 'center' : 'flex-start',
              alignItems: 'center',
              padding: 0,
              borderRadius: '8px',
              margin: '0px',
              height: '48px',
              textAlign: icon ? 'left' : 'center',
              backgroundColor: `${selected ? theme.palette.button.hover : 'transparent'}`,
              [':hover']: {
                backgroundColor: theme.palette.button.hover,
              },
              [':active']: {
                backgroundColor: theme.palette.button.pressed,
              },
            }}
            aria-label={ariaLabel ?? text}
          >
            {icon && (
              <ListItemIcon
                sx={{
                  justifyContent: text || !open ? 'center' : 'flex-end',
                  alignItems: 'center',
                  minWidth: 'unset',
                  height: '24px',
                  width: '24px',
                  padding: '12px',
                  ['svg']: {
                    fill: `${disabled ? disabledTextColor : textColor} !important`,
                    fontSize: '24px',
                  },
                }}
              >
                {icon}
              </ListItemIcon>
            )}

            {text ? (
              <Collapse
                in={open}
                orientation="horizontal"
                timeout={SIDEBAR.transition.duration}
                sx={{
                  flex: 1,
                  padding: '12px',
                  boxSizing: 'border-box',
                  alignItems: 'center',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: `all ${SIDEBAR.transition.durationMs} ${SIDEBAR.transition.easing}`,
                }}
              >
                <ListItemText
                  primary={displayText ?? text}
                  slotProps={{
                    primary: {
                      color: disabled ? disabledTextColor : textColor,
                      fontSize: '16px !important',
                      fontWeight: 350,
                      lineHeight: '24px',
                      letterSpacing: '0',
                    },
                  }}
                  sx={{
                    margin: 0,
                  }}
                />
              </Collapse>
            ) : null}
          </ListItemButton>
        </Box>
      </Tooltip>
    </ListItem>
  );
};
